const SAMPLE_WINDOW_MS = 1000;
const SAMPLE_INTERVAL_MS = 50;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const remoteAudioTracks = (room) => {
  const tracks = [];
  room?.remoteParticipants?.forEach((participant) => {
    participant.audioTrackPublications?.forEach((publication) => {
      const mediaStreamTrack = publication.track?.mediaStreamTrack;
      if (mediaStreamTrack) tracks.push(mediaStreamTrack);
    });
  });
  return tracks;
};

/**
 * Peak level of the remote voices as they arrive in the session's own audio
 * graph, 0..1, with the context state and track count beside it.
 *
 * The browser cannot report the speaker's actual volume — no web API exposes
 * it — so this answers the question that volume would be a poor proxy for:
 * was any sound reaching the output at all? Three outcomes stay distinct,
 * which is the point:
 *
 *   trackCount 0                → nobody was publishing audio
 *   level 0, context suspended  → the output is parked (the OKO2-4062 failure)
 *   level 0, context running    → a live path that simply had no sound in it
 *
 * A zero is therefore ambiguous on its own: the window may have landed between
 * phrases, and the bot's inbound track carries no packets while it is quiet.
 * `sampledMs` is reported so a short window is never mistaken for real silence.
 *
 * Measured on the room's own context rather than a fresh one, so a parked
 * output reads as zero instead of looking healthy, and tapped ahead of
 * LiveKit's gain, so a deliberately muted or ducked track still reports the
 * signal that reached us. Nothing is connected to the destination, so
 * measuring can never duplicate the audio.
 */
export async function sampleOutputLevel(room, windowMs = SAMPLE_WINDOW_MS) {
  const context = room?.audioContext;
  const tracks = remoteAudioTracks(room);
  const unmeasured = {
    level: null,
    trackCount: tracks.length,
    contextState: context?.state ?? null,
    sampledMs: 0,
  };

  if (!context || typeof context.createAnalyser !== "function") return unmeasured;
  if (tracks.length === 0) return unmeasured;
  if (typeof MediaStream === "undefined") return unmeasured;

  const analyser = context.createAnalyser();
  analyser.fftSize = 256;
  const sources = [];
  const release = () => {
    sources.forEach((source) => source.disconnect());
    analyser.disconnect();
  };

  try {
    tracks.forEach((track) => {
      const source = context.createMediaStreamSource(new MediaStream([track]));
      source.connect(analyser);
      sources.push(source);
    });
  } catch (_ignored) {
    release();
    return unmeasured;
  }

  const data = new Uint8Array(analyser.frequencyBinCount);
  const samples = Math.max(1, Math.ceil(windowMs / SAMPLE_INTERVAL_MS));
  const startedAt = Date.now();
  let peak = 0;

  try {
    for (let i = 0; i < samples; i += 1) {
      analyser.getByteFrequencyData(data);
      for (let bin = 0; bin < data.length; bin += 1) {
        if (data[bin] > peak) peak = data[bin];
      }
      if (i < samples - 1) await sleep(SAMPLE_INTERVAL_MS);
    }
  } finally {
    release();
  }

  return {
    level: Math.round(Math.min(peak / 255, 1) * 1000) / 1000,
    trackCount: tracks.length,
    contextState: context.state ?? null,
    sampledMs: Date.now() - startedAt,
  };
}
